import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import ManifestLink from './components/pwa/ManifestLink';
import ProtectedRoute from './components/ProtectedRoute';
import { TabsProvider } from './components/shell/TabsContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TenantRegisterPage from './pages/TenantRegisterPage';
import TenantLoginPage from './pages/TenantLoginPage';
import SetupPage from './pages/SetupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ProfileSettings from './pages/ProfileSettings';
import ChangePasswordPage from './pages/ChangePasswordPage';

// Super Admin Pages
import SuperAdminDashboard from './pages/super-admin/Dashboard';
import SuperAdminUserManagement from './pages/super-admin/UserManagement';
import StorageConfig from './pages/super-admin/StorageConfig';
import SMTPConfig from './pages/super-admin/SMTPConfig';
import RailwaySettings from './pages/super-admin/RailwaySettings';
import PlatformDomains from './pages/super-admin/PlatformDomains';
import BackupRestore from './pages/super-admin/BackupRestore';
import StudentDiary from './pages/diary/StudentDiary';
import Attendance from './pages/attendance/Attendance';
import MyAttendance from './pages/attendance/MyAttendance';

// Tenant Admin Pages
import TenantAdminDashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import UserForm from './pages/admin/UserForm';
import CRMPage from './pages/crm/CRMPage';
import LeadForm from './pages/crm/LeadForm';
import LeadDetail from './pages/crm/LeadDetail';
import PublicLeadCapture from './pages/crm/PublicLeadCapture';
import SuperAdminCRMOverview from './pages/crm/SuperAdminCRMOverview';
import AdminCourseManagement from './pages/admin/CourseManagement';
import InstructorManagement from './pages/admin/InstructorManagement';
import TenantSettings from './pages/admin/TenantSettings';
import RoleManagement from './pages/admin/RoleManagement';

// Instructor Pages
import InstructorDashboard from './pages/instructor/Dashboard';
import InstructorCourses from './pages/instructor/Courses';
import CourseView from './pages/instructor/CourseView';
import CourseBuilder from './pages/instructor/CourseBuilder';
import CourseStructureBuilder from './pages/instructor/CourseStructureBuilder';
import NodeEditor from './pages/instructor/NodeEditor';
import QuestionBank from './pages/instructor/QuestionBank';
import QuestionForm from './pages/instructor/QuestionForm';
import ExamList from './pages/instructor/ExamList';
import ExamBuilder from './pages/instructor/ExamBuilder';
import ExamReport from './pages/instructor/ExamReport';
import ExamPreview from './pages/instructor/ExamPreview';
import { PreviewLearnerProvider } from './components/PreviewMode';
import Batches from './pages/instructor/Batches';
import Community from './pages/instructor/Community';
import BatchDetail from './pages/instructor/BatchDetail';
import StudentAnalytics from './pages/instructor/StudentAnalytics';
import { HostAwareRoot, HostAwareLogin } from './components/HostAwareRoutes';

// Learner Pages
import LearnerDashboard from './pages/learner/Dashboard';
import CourseCatalog from './pages/learner/Catalog';
import CoursePlayer from './pages/learner/CoursePlayer';
import CoursePlayerTree from './pages/learner/CoursePlayerTree';
import CoursePerformance from './pages/learner/CoursePerformance';
import ExamTaker from './pages/learner/ExamTaker';
import ExamResults from './pages/learner/ExamResults';
import LearnerExamList from './pages/learner/ExamList';
import LearnerCertificates from './pages/learner/Certificates';

// Impersonation
import ImpersonatePage from './pages/ImpersonatePage';

import './index.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Points the install at whichever tenant portal this is. */}
        <ManifestLink />
        <TabsProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HostAwareRoot />} />
          <Route path="/login" element={<HostAwareLogin />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register-tenant" element={<TenantRegisterPage />} />
          <Route path="/tenant/:subdomain/login" element={<TenantLoginPage />} />
          <Route path="/tenant/:subdomain" element={<TenantLoginPage />} />
          <Route path="/setup" element={<SetupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/tenant/:subdomain/enquire" element={<PublicLeadCapture />} />
          
          {/* Impersonation route */}
          <Route path="/impersonate" element={<ImpersonatePage />} />
          <Route path="/tenant/:subdomain/impersonate" element={<ImpersonatePage />} />
          <Route path="/super-admin/impersonate" element={<ImpersonatePage />} />

          {/* Profile Settings - All authenticated users */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'tenant_admin', 'instructor', 'learner']}>
                <ProfileSettings />
              </ProtectedRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/tenants"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/storage"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <StorageConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/smtp"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SMTPConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/domains"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <PlatformDomains />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/railway"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <RailwaySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin', 'instructor']}>
                <Attendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/student"
            element={
              <ProtectedRoute allowedRoles={['parent', 'tenant_admin', 'instructor']}>
                <MyAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/attendance"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <MyAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/diary"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin', 'instructor', 'parent']}>
                <StudentDiary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/diary"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <StudentDiary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/backup"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <BackupRestore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/users"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminUserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/users/new"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <UserForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/crm"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminCRMOverview />
              </ProtectedRoute>
            }
          />

          {/* Tenant Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <TenantAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/new"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin', 'instructor']}>
                <UserForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/courses"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <AdminCourseManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <TenantAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/instructors"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <InstructorManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <RoleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <TenantSettings />
              </ProtectedRoute>
            }
          />

          {/* Instructor Routes */}
          <Route
            path="/instructor"
            element={
              <ProtectedRoute allowedRoles={['instructor']}>
                <InstructorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses"
            element={
              <ProtectedRoute allowedRoles={['instructor']}>
                <InstructorCourses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses/new"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <CourseBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses/:courseId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <CourseView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses/:courseId/students/:studentId/analytics"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <StudentAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses/:courseId/edit"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <CourseBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses/:courseId/structure"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <CourseStructureBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses/:courseId/nodes/:nodeId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <NodeEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/students"
            element={
              <ProtectedRoute allowedRoles={['instructor']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'tenant_admin', 'instructor']}>
                <CRMPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/leads/new"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'tenant_admin', 'instructor']}>
                <LeadForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/leads/:leadId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'tenant_admin', 'instructor']}>
                <LeadDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'tenant_admin']}>
                <CRMPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/questions"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <QuestionBank />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/questions/new"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <QuestionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/questions/:questionId/edit"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <QuestionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/exams"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <ExamList />
              </ProtectedRoute>
            }
          />
          {/* "View as a student" window: the signed-in staff member is presented as a learner */}
          <Route
            path="/preview/course/:courseId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin', 'super_admin']}>
                <PreviewLearnerProvider><CoursePlayerTree /></PreviewLearnerProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/preview/exams/:examId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin', 'super_admin']}>
                <PreviewLearnerProvider><ExamPreview learnerView /></PreviewLearnerProvider>
              </ProtectedRoute>
            }
          />
          {['/instructor/batches', '/admin/batches'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                  <Batches />
                </ProtectedRoute>
              }
            />
          ))}
          {['/instructor/batches/:batchId', '/admin/batches/:batchId'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                  <BatchDetail />
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="/instructor/exams/:examId/preview"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <ExamPreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/exams/:examId/report"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <ExamReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/exams/new"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <ExamBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/exams/:examId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <ExamBuilder />
              </ProtectedRoute>
            }
          />

          {/* Learner Routes */}
          <Route
            path="/learn"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <LearnerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/my-courses"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <LearnerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/catalog"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <CourseCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/course/:courseId"
            element={
              <ProtectedRoute allowedRoles={['learner', 'instructor', 'tenant_admin']}>
                <CoursePlayerTree />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/course/:courseId/performance"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <CoursePerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/course/:courseId/exams"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <LearnerExamList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/exams/:examId"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <ExamTaker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/community"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'tenant_admin']}>
                <Community />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/community"
            element={
              <ProtectedRoute allowedRoles={['tenant_admin']}>
                <Community />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/exams/:examId/results"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <ExamResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/certificates"
            element={
              <ProtectedRoute allowedRoles={['learner']}>
                <LearnerCertificates />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </TabsProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
